import { DCUIContent, DCUIContentAcuantValidation } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { ErrorAlert } from "@containers/common/error-alert/error-alert";
import { TabTitleWithErrorCount } from "@containers/common/tab-title-with-error-count/tab-title-with-error-count";
import { DCUIThemingTextContentValidationTypes } from "@enums/dcui-theming-text-content-validation-types";
import { BadResolution, BlurDetection, DCUIThemingTextContentAcuantValidationTypes, DetectionValidationHeader, GlareDetection, LivenessDetection } from "@enums/dcui-theming-text-content-acuant-validation-types";
import {
    DCUIThemingTextContentAcuantValidationSectionValidationErrors,
    DCUIThemingTextContentValidationErrors,
    DCUIThemingTextContentValidationSectionValidationErrors
} from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { ActiveAcuantPropertyFlags } from "@idw-types/common/active-acuant-property-flags";

export const getDCUIThemingTextContentValidationErrors = (
    isUsingDCUIVersion2: boolean,
    hasDisplayableDocumentCaptureAttributes: boolean,
    activeAcuantProperties: ActiveAcuantPropertyFlags,
    dcuiContent: DCUIContent,
): DCUIThemingTextContentValidationErrors => {
    let count = 0;
    let continueButtonError = null;
    let discardButtonError = null;
    let capturingTextError = null;
    let processingTextError = null;
    let relaunchCaptureLabelTextError = null;
    let relaunchCaptureButtonTextError = null;
    let captureErrorTextError = null;
    let iosSafariBrowserWarningTextError = null;
    let desktopSafariBrowserWarningTextError = null;
    let androidChromeBrowserWarningTextError = null;
    let desktopChromeBrowserWarningTextError = null;
    let validationTitleError = null;
    let validationLoadingTextError = null;
    let cameraNoneError = null;
    let cameraSmallDocumentError = null;
    let cameraBigDocumentError = null;
    let cameraTapToCaptureError = null;

    const defaultValidationSectionValidationErrors: DCUIThemingTextContentValidationSectionValidationErrors = {
        detectionTitleError: null,
        detectionLoadingTextError: null,
        detectionSuccessTextError: null,
        detectionErrorTextError: null
    };
    const validationSectionsValidationErrors = {
        [DCUIThemingTextContentValidationTypes.LANDSCAPE]: { ...defaultValidationSectionValidationErrors },
        [DCUIThemingTextContentValidationTypes.PORTRAIT]: { ...defaultValidationSectionValidationErrors }
    };

    const defaultAcuantValidationSectionValidationErrors: DCUIThemingTextContentAcuantValidationSectionValidationErrors = {
        detectionValidationHeaderTextError: null,
        detectionSuccessMessageError: null,
        detectionErrorMessageError: null
    };
    const acuantValidationSectionsValidationErrors = {
        [DCUIThemingTextContentAcuantValidationTypes.BAD_RESOLUTION]: { ...defaultAcuantValidationSectionValidationErrors },
        [DCUIThemingTextContentAcuantValidationTypes.BLUR_DETECTION]: { ...defaultAcuantValidationSectionValidationErrors },
        [DCUIThemingTextContentAcuantValidationTypes.GLARE_DETECTION]: { ...defaultAcuantValidationSectionValidationErrors },
        [DCUIThemingTextContentAcuantValidationTypes.LIVENESS_DETECTION]: { ...defaultAcuantValidationSectionValidationErrors }
    };

    if (isUsingDCUIVersion2 && hasDisplayableDocumentCaptureAttributes) {
        if (!dcuiContent.general.continueButtonText) {
            count += 1;
            continueButtonError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.general.discardButtonText) {
            count += 1;
            discardButtonError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.acuant.camera.capturingText) {
            count += 1;
            capturingTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.acuant.camera.processingText) {
            count += 1;
            processingTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.acuant.relaunchCaptureLabelText) {
            count += 1;
            relaunchCaptureLabelTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.acuant.relaunchCaptureButtonText) {
            count += 1;
            relaunchCaptureButtonTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.acuant.errorMessage) {
            count += 1;
            captureErrorTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.acuant.iosSafariBrowserWarningText) {
            count += 1;
            iosSafariBrowserWarningTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.acuant.desktopSafariBrowserWarningText) {
            count += 1;
            desktopSafariBrowserWarningTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.acuant.androidChromeBrowserWarningText) {
            count += 1;
            androidChromeBrowserWarningTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.acuant.desktopChromeBrowserWarningText) {
            count += 1;
            desktopChromeBrowserWarningTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.validation.validationTitle) {
            count += 1;
            validationTitleError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }
        if (!dcuiContent.validation.validationLoadingText) {
            count += 1;
            validationLoadingTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
        }

        Object.values(DCUIThemingTextContentValidationTypes).forEach(validationType => {
            if (!dcuiContent.validation[`${validationType}DetectionSubheading`]) {
                count += 1;
                validationSectionsValidationErrors[validationType].detectionTitleError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
            }
            if (!dcuiContent.validation[`${validationType}DetectionLoadingText`]) {
                count += 1;
                validationSectionsValidationErrors[validationType].detectionLoadingTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
            }
            if (!dcuiContent.validation[`${validationType}DetectionSuccessMessage`]) {
                count += 1;
                validationSectionsValidationErrors[validationType].detectionSuccessTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
            }
            if (!dcuiContent.validation[`${validationType}DetectionErrorMessage`]) {
                count += 1;
                validationSectionsValidationErrors[validationType].detectionErrorTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
            }
        });

        Object.values(DCUIThemingTextContentAcuantValidationTypes).forEach(acuantValidationType => {
            let hasProperty = false;
            let detectionValidationHeaderText: keyof DCUIContentAcuantValidation;
            let detectionSuccessMessage: keyof DCUIContentAcuantValidation;
            let detectionErrorMessage: keyof DCUIContentAcuantValidation;

            switch (acuantValidationType) {
                case DCUIThemingTextContentAcuantValidationTypes.BAD_RESOLUTION:
                    hasProperty = activeAcuantProperties.hasValidAcuantDPIValidationProperty;
                    detectionValidationHeaderText = `${DetectionValidationHeader.LOW_DPI}DetectionValidationHeader`;
                    detectionSuccessMessage = `${BadResolution.VALID}Message`;
                    detectionErrorMessage = `${BadResolution.INVALID}Message`;
                    break;
                case DCUIThemingTextContentAcuantValidationTypes.BLUR_DETECTION:
                    hasProperty = activeAcuantProperties.hasValidAcuantSharpnessValidationProperty;
                    detectionValidationHeaderText = `${DetectionValidationHeader.LOW_SHARPNESS}DetectionValidationHeader`;
                    detectionSuccessMessage = `${BlurDetection.VALID}Message`;
                    detectionErrorMessage = `${BlurDetection.INVALID}Message`;
                    break;
                case DCUIThemingTextContentAcuantValidationTypes.GLARE_DETECTION:
                    hasProperty = activeAcuantProperties.hasValidAcuantGlareValidationProperty;
                    detectionValidationHeaderText = `${DetectionValidationHeader.GLARE}DetectionValidationHeader`;
                    detectionSuccessMessage = `${GlareDetection.VALID}Message`;
                    detectionErrorMessage = `${GlareDetection.INVALID}Message`;
                    break;
                case DCUIThemingTextContentAcuantValidationTypes.LIVENESS_DETECTION:
                    hasProperty = activeAcuantProperties.hasValidLivenessApikey;
                    detectionValidationHeaderText = `${DetectionValidationHeader.LIVENESS}DetectionValidationHeader`;
                    detectionSuccessMessage = `${LivenessDetection.VALID}Message`;
                    detectionErrorMessage = `${LivenessDetection.INVALID}Message`;
                    break;
            }

            if (hasProperty) {
                if (!dcuiContent.acuant.validation[detectionValidationHeaderText]) {
                    count += 1;
                    acuantValidationSectionsValidationErrors[acuantValidationType].detectionValidationHeaderTextError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
                }
                if (!dcuiContent.acuant.validation[detectionSuccessMessage]) {
                    count += 1;
                    acuantValidationSectionsValidationErrors[acuantValidationType].detectionSuccessMessageError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
                }
                if (!dcuiContent.acuant.validation[detectionErrorMessage]) {
                    count += 1;
                    acuantValidationSectionsValidationErrors[acuantValidationType].detectionErrorMessageError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
                }
            }
        });

        if (activeAcuantProperties.hasValidAcuantSDKCaptureProperty) {
            if (!dcuiContent.acuant.camera.none) {
                count += 1;
                cameraNoneError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
            }
            if (!dcuiContent.acuant.camera.smallDocument) {
                count += 1;
                cameraSmallDocumentError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
            }
            if (!dcuiContent.acuant.camera.bigDocument) {
                count += 1;
                cameraBigDocumentError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
            }
            if (!dcuiContent.acuant.camera.tapToCature) {
                count += 1;
                cameraTapToCaptureError = <ErrorAlert><span>{resources.common.requiredField}</span></ErrorAlert >;
            }
        }
    }

    return {
        count: count,
        continueButtonError,
        discardButtonError,
        capturingTextError,
        processingTextError,
        relaunchCaptureLabelTextError,
        relaunchCaptureButtonTextError,
        captureErrorTextError,
        iosSafariBrowserWarningTextError,
        desktopSafariBrowserWarningTextError,
        androidChromeBrowserWarningTextError,
        desktopChromeBrowserWarningTextError,
        validationTitleError,
        validationLoadingTextError,
        ...validationSectionsValidationErrors,
        ...acuantValidationSectionsValidationErrors,
        cameraNoneError,
        cameraSmallDocumentError,
        cameraBigDocumentError,
        cameraTapToCaptureError,
        tabTitle: <TabTitleWithErrorCount
            tabName={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.textContent.tabName}
            errorCount={count}
        />
    };
};